import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Spin, Table, Tag, Tabs, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { adminOperationsService, adminRoomService, adminRoomWorkflowService } from '../../services/adminService';

interface StaffMember {
  id: string;
  full_name: string;
  role_code?: string;
  role_name?: string;
  department?: string;
}

interface Room {
  id: string;
  room_number: string;
}

interface HousekeepingTask {
  id: string;
  room_id: string;
  room_number?: string;
  assigned_to?: string;
  status: 'pending' | 'in_progress' | 'completed';
  note?: string;
  created_at: string;
  completed_at?: string;
}

interface MaintenanceIssue {
  id: string;
  room_id: string;
  room_number?: string;
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  assigned_to?: string;
  created_at: string;
}

const RoomWorkflows: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [housekeepingTasks, setHousekeepingTasks] = useState<HousekeepingTask[]>([]);
  const [maintenanceIssues, setMaintenanceIssues] = useState<MaintenanceIssue[]>([]);
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);

  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [issueModalVisible, setIssueModalVisible] = useState(false);
  const [taskForm] = Form.useForm();
  const [issueForm] = Form.useForm();

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      setLoading(true);
      const [roomsRes, tasksRes, issuesRes, staffRes] = await Promise.all([
        adminRoomService.getAllRooms(),
        adminRoomWorkflowService.getHousekeepingTasks(),
        adminRoomWorkflowService.getMaintenanceIssues(),
        // the assignee picker must not break the whole screen if the roster fails
        adminOperationsService.getStaffMembers().catch(() => null),
      ]);

      setRooms(Array.isArray(roomsRes.data) ? (roomsRes.data as Room[]) : []);
      setHousekeepingTasks(Array.isArray(tasksRes.data) ? (tasksRes.data as HousekeepingTask[]) : []);
      setMaintenanceIssues(Array.isArray(issuesRes.data) ? (issuesRes.data as MaintenanceIssue[]) : []);
      setStaffMembers(
        staffRes && Array.isArray(staffRes.data) ? (staffRes.data as StaffMember[]) : [],
      );
    } catch (error) {
      console.error('Error initializing room workflows:', error);
      message.error('Failed to load room workflow data');
    } finally {
      setLoading(false);
    }
  };

  const refreshTasks = async () => {
    try {
      const response = await adminRoomWorkflowService.getHousekeepingTasks();
      setHousekeepingTasks(Array.isArray(response.data) ? (response.data as HousekeepingTask[]) : []);
    } catch (error) {
      console.error('Error refreshing housekeeping tasks:', error);
      message.error('Failed to refresh housekeeping tasks');
    }
  };

  const refreshIssues = async () => {
    try {
      const response = await adminRoomWorkflowService.getMaintenanceIssues();
      setMaintenanceIssues(Array.isArray(response.data) ? (response.data as MaintenanceIssue[]) : []);
    } catch (error) {
      console.error('Error refreshing maintenance issues:', error);
      message.error('Failed to refresh maintenance issues');
    }
  };

  const createTask = async (values: any) => {
    try {
      await adminRoomWorkflowService.createHousekeepingTask(values);
      message.success('Housekeeping task created');
      setTaskModalVisible(false);
      taskForm.resetFields();
      refreshTasks();
    } catch (error) {
      console.error('Error creating housekeeping task:', error);
      message.error('Failed to create housekeeping task');
    }
  };

  const createIssue = async (values: any) => {
    try {
      await adminRoomWorkflowService.createMaintenanceIssue(values);
      message.success('Maintenance issue created');
      setIssueModalVisible(false);
      issueForm.resetFields();
      refreshIssues();
    } catch (error) {
      console.error('Error creating maintenance issue:', error);
      message.error('Failed to create maintenance issue');
    }
  };

  const updateTaskStatus = async (task: HousekeepingTask, status: HousekeepingTask['status']) => {
    try {
      await adminRoomWorkflowService.updateHousekeepingTaskStatus(task.id, status);
      message.success('Task status updated');
      refreshTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
      message.error('Failed to update task status');
    }
  };

  const updateIssueStatus = async (issue: MaintenanceIssue, status: MaintenanceIssue['status']) => {
    try {
      await adminRoomWorkflowService.updateMaintenanceIssue(issue.id, { status });
      message.success('Issue status updated');
      refreshIssues();
    } catch (error) {
      console.error('Error updating issue status:', error);
      message.error('Failed to update issue status');
    }
  };

  const assignTask = async (task: HousekeepingTask, assignedTo: string | null) => {
    try {
      await adminRoomWorkflowService.assignHousekeepingTask(task.id, assignedTo);
      message.success(assignedTo ? `Task assigned to ${assignedTo}` : 'Task unassigned');
      refreshTasks();
    } catch (error) {
      console.error('Error assigning housekeeping task:', error);
      message.error('Failed to assign housekeeping task');
    }
  };

  // Housekeeping staff first; if the roster has none yet, offer everyone so
  // a task can always be assigned.
  const housekeepingStaff = staffMembers.filter((member) => {
    const haystack = `${member.role_code || ''} ${member.role_name || ''} ${member.department || ''}`.toLowerCase();
    return haystack.includes('housekeep');
  });
  const staffOptions = Array.from(
    new Set(
      (housekeepingStaff.length > 0 ? housekeepingStaff : staffMembers).map((member) => member.full_name),
    ),
  ).map((name) => ({ label: name, value: name }));

  const taskColumns = [
    {
      title: 'Room',
      dataIndex: 'room_number',
      key: 'room_number',
      render: (_: string, record: HousekeepingTask) => record.room_number || record.room_id,
    },
    {
      title: 'Assigned To',
      dataIndex: 'assigned_to',
      key: 'assigned_to',
      render: (value: string) => value || 'Unassigned',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: HousekeepingTask['status']) => (
        <Tag color={status === 'completed' ? 'green' : status === 'in_progress' ? 'blue' : 'orange'}>{status}</Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value: string) => value ? dayjs(value).format('MMM D, YYYY h:mm A') : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: HousekeepingTask) => (
        <Space wrap>
          <Select
            size="small"
            style={{ width: 140 }}
            value={record.status}
            onChange={(nextStatus) => updateTaskStatus(record, nextStatus)}
            options={[
              { label: 'Pending', value: 'pending' },
              { label: 'In Progress', value: 'in_progress' },
              { label: 'Completed', value: 'completed' },
            ]}
          />
          <Select
            size="small"
            style={{ width: 160 }}
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="Assign staff"
            value={record.assigned_to || undefined}
            onChange={(nextValue) => assignTask(record, nextValue ?? null)}
            options={staffOptions}
          />
        </Space>
      ),
    },
  ];

  const issueColumns = [
    {
      title: 'Room',
      dataIndex: 'room_number',
      key: 'room_number',
      render: (_: string, record: MaintenanceIssue) => record.room_number || record.room_id,
    },
    {
      title: 'Issue',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: MaintenanceIssue['priority']) => (
        <Tag color={priority === 'urgent' ? 'red' : priority === 'high' ? 'volcano' : priority === 'medium' ? 'gold' : 'blue'}>
          {priority}
        </Tag>
      ),
    },
    {
      title: 'Assigned To',
      dataIndex: 'assigned_to',
      key: 'assigned_to',
      render: (value: string) => value || 'Unassigned',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: MaintenanceIssue['status']) => (
        <Tag color={status === 'resolved' || status === 'closed' ? 'green' : status === 'in_progress' ? 'blue' : 'orange'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: MaintenanceIssue) => (
        <Select
          size="small"
          style={{ width: 140 }}
          value={record.status}
          onChange={(nextStatus) => updateIssueStatus(record, nextStatus)}
          options={[
            { label: 'Open', value: 'open' },
            { label: 'In Progress', value: 'in_progress' },
            { label: 'Resolved', value: 'resolved' },
            { label: 'Closed', value: 'closed' },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ marginBottom: 4 }}>Room Workflows</h1>
        <p style={{ margin: 0, color: '#666' }}>Manage housekeeping operations and maintenance tracking.</p>
      </div>

      <Spin spinning={loading}>
        <Tabs
          items={[
            {
              key: 'housekeeping',
              label: 'Housekeeping',
              children: (
                <div>
                  <Space style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setTaskModalVisible(true)}>
                      New Task
                    </Button>
                    <Button onClick={refreshTasks}>Refresh</Button>
                  </Space>
                  <Table rowKey="id" columns={taskColumns} dataSource={housekeepingTasks} pagination={{ pageSize: 10 }} scroll={{ x: 'max-content' }} />
                </div>
              ),
            },
            {
              key: 'maintenance',
              label: 'Maintenance',
              children: (
                <div>
                  <Space style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setIssueModalVisible(true)}>
                      New Issue
                    </Button>
                    <Button onClick={refreshIssues}>Refresh</Button>
                  </Space>
                  <Table rowKey="id" columns={issueColumns} dataSource={maintenanceIssues} pagination={{ pageSize: 10 }} scroll={{ x: 'max-content' }} />
                </div>
              ),
            },
          ]}
        />
      </Spin>

      <Modal
        title="Create Housekeeping Task"
        open={taskModalVisible}
        onCancel={() => setTaskModalVisible(false)}
        onOk={() => taskForm.submit()}
      >
        <Form form={taskForm} layout="vertical" onFinish={createTask}>
          <Form.Item name="room_id" label="Room" rules={[{ required: true, message: 'Room is required' }]}>
            <Select
              options={rooms.map((room) => ({ label: room.room_number, value: room.id }))}
              placeholder="Select room"
            />
          </Form.Item>
          <Form.Item name="assigned_to" label="Assign Staff">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="Select a staff member"
              options={staffOptions}
            />
          </Form.Item>
          <Form.Item name="note" label="Task Note">
            <Input.TextArea rows={3} placeholder="What should be done for this room" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Create Maintenance Issue"
        open={issueModalVisible}
        onCancel={() => setIssueModalVisible(false)}
        onOk={() => issueForm.submit()}
      >
        <Form form={issueForm} layout="vertical" onFinish={createIssue}>
          <Form.Item name="room_id" label="Room" rules={[{ required: true, message: 'Room is required' }]}>
            <Select
              options={rooms.map((room) => ({ label: room.room_number, value: room.id }))}
              placeholder="Select room"
            />
          </Form.Item>
          <Form.Item name="title" label="Issue Title" rules={[{ required: true, message: 'Issue title is required' }]}>
            <Input placeholder="e.g. AC not cooling" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Add issue details" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue="medium">
            <Select
              options={[
                { label: 'Low', value: 'low' },
                { label: 'Medium', value: 'medium' },
                { label: 'High', value: 'high' },
                { label: 'Urgent', value: 'urgent' },
              ]}
            />
          </Form.Item>
          <Form.Item name="assigned_to" label="Assigned To">
            <Input placeholder="Technician name" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoomWorkflows;
